import { getAllMapObjectsMapObjectsGet } from "../client";
import type { MapObjectResponse as MapObjectApiResponse } from "../client/types.gen";

export interface OwnerInfo {
  user_id: number;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  photo_url: string | null;
  level: number;
  exp: number;
  exp_needed: number;
  vip_until?: string | null; // ISO datetime string або null
}

/**
 * Free-form backend `data` bag on a map object. Keys are ad-hoc per object type
 * (house meta, admin transforms, likes history, etc.). Known fields used by the
 * frontend are listed so call sites stay typed without `any`.
 */
export interface MapObjectData {
  real_estate_id?: number;
  mirror_h?: boolean;
  rotate_180?: boolean;
  likes_count?: number;
  viewed?: number;
  is_liked?: boolean;
  mirror_direction?: string;
  owner_message?: string;
  owner_last_active_at?: string;
  owner_absence_reset_at?: string;
  furniture_count?: number;
  cobweb_count?: number;
  created_at?: string;
  heritage?: boolean;
  /** Like history rows — shape matches UI LikeHistoryEntry; kept structural here. */
  likes_history?: Array<{
    user: {
      user_id: number;
      first_name: string | null;
      last_name: string | null;
      username: string | null;
      photo_url: string | null;
      level: number;
      exp_current: number;
      exp_needed: number;
      currency_balance: number;
      total_taps: number;
      vip_until: string | null;
    };
    created_at: string;
  }>;
}

export interface MapObject {
  id: number;
  type: string; // "plot" | "house" | "cityhall" | "tree" | "shop" тощо
  x: number;
  y: number;
  owner_id: number | null;
  owner: OwnerInfo | null; // Інформація про власника (якщо є)
  data: MapObjectData;
  // Поля для будинків (type === "house")
  likes_count?: number; // Загальна кількість лайків (тільки для будинків)
  viewed?: number; // Кількість переглядів будинку (тільки для будинків)
  is_liked?: boolean; // Чи поставив поточний користувач лайк (тільки для будинків)
  mirror_direction?: "left" | "right"; // Напрямок відзеркалення будинку (тільки для будинків)
  display_width?: number | null;
  display_height?: number | null;
}

/**
 * Structural normalization of a map-object API response: lifts `owner_data` to
 * `owner`/`owner_id` and surfaces house-only fields from `data`. Not a rename.
 */
export const normalizeMapObject = (raw: MapObjectApiResponse): MapObject => {
  const owner: OwnerInfo | null = (raw.owner_data ?? null) as OwnerInfo | null;
  const owner_id: number | null = owner ? owner.user_id : null;

  // OpenAPI types data as `{ [key: string]: unknown }`; cast to our known bag.
  const data = { ...(raw.data ?? {}) } as MapObjectData;

  const base: MapObject = {
    id: raw.id,
    type: raw.type,
    x: raw.x,
    y: raw.y,
    owner_id,
    owner,
    data,
    display_width: raw.display_width ?? null,
    display_height: raw.display_height ?? null,
  };

  if (raw.type === "house") {
    const mirror_direction =
      raw.mirror_direction
      ?? data.mirror_direction
      ?? "left";
    return {
      ...base,
      likes_count: data.likes_count,
      viewed: data.viewed,
      is_liked: data.is_liked,
      mirror_direction: mirror_direction as "left" | "right",
    };
  }

  return base;
};

const normalizeMapObjects = (raw: MapObjectApiResponse[]): MapObject[] =>
  raw.map(normalizeMapObject);

/**
 * Отримати всі об'єкти на карті (ділянки, будинки, мерії тощо)
 */
export const getMapObjects = async (): Promise<MapObject[]> => {
  const { data } = await getAllMapObjectsMapObjectsGet({ throwOnError: true });
  return normalizeMapObjects(data);
};
